import React, { useState, useEffect, useRef } from "react";
import "./chatBox.css";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { FiCopy, FiVolume2, FiStopCircle } from "react-icons/fi";
import { MdOutlineFileDownload } from "react-icons/md";
import { RiEditLine } from "react-icons/ri";
import { Document, Packer, Paragraph, TextRun } from "docx";
import { saveAs } from "file-saver";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

const suggestionsList = [
  "What is the high-level summary of this publication?",
  "What are the primary results or key findings from this publication?",
  "What was the study design or approach used in this publication?",
  "What are the major strengths and limitations of this publication?",
  "What are the final conclusions of this publication?",
];

const ChatBox = ({ publicationId, setHighlightData }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [speakingIndex, setSpeakingIndex] = useState(null);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState({ inProgress: false, error: false });
  const token = localStorage.getItem("authToken");
  const speechSynthesisRef = useRef(null);
 
  const speechSettings = {
    lang: "en-US",    // Set language/accent
    rate: 1,          // Speed (0.5 - 2)
    pitch: 1,         // Pitch (0 - 2)
    volume: 1         // Volume (0 - 1)
  };

  useEffect(() => {
    setSpeechSupported('speechSynthesis' in window);
    
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        setVoicesLoaded(true);
      }
    };
    
    loadVoices();
    
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
    
    const fetchChatHistory = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/chat_history?file_id=${publicationId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch  ");
        const history = await response.json();
        if (Array.isArray(history) && history.length > 0) {
          const formatted = history.map((item) => ({
            type: item.sender === "user_message" ? "user" : "bot",
            text: item.message,
            time: item.time,
          }));
          setMessages(formatted); 
        }
      } catch (error) {
        console.error("Error fetching history:", error);
      }
    };
    fetchChatHistory();
 
    return () => {
      if (speechSynthesisRef.current) {
        window.speechSynthesis.cancel();
      }
    };
  }, [publicationId, token]);

  const getFemaleVoice = () => {
    if (!voicesLoaded) return null;
    
    const voices = window.speechSynthesis.getVoices();
    
    const femaleVoice = voices.find(voice => 
      voice.lang.includes(speechSettings.lang) && 
      (voice.name.includes('Female') || 
       voice.name.includes('Zira') || 
       voice.name.includes('Susan') ||
       voice.name.includes('Karen') ||
       voice.name.includes('Samantha') ||
       voice.name.includes('Moira'))
    );
    
    if (!femaleVoice) {
      const langVoice = voices.find(voice => 
        voice.lang.includes(speechSettings.lang)
      );
      return langVoice || null;
    }
    
    return femaleVoice;
  };

  const handleSpeak = (text, index) => {
    if (!speechSupported || !voicesLoaded) return;
 
    if (speakingIndex === index) {
      window.speechSynthesis.cancel();
      setSpeakingIndex(null);
      return;
    }
 
    window.speechSynthesis.cancel();
 
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = speechSettings.lang;
    utterance.rate = speechSettings.rate;
    utterance.pitch = speechSettings.pitch;
    utterance.volume = speechSettings.volume;
    
    const femaleVoice = getFemaleVoice();
    if (femaleVoice) {
      utterance.voice = femaleVoice;
    }
    
    utterance.onend = () => setSpeakingIndex(null);
    utterance.onerror = () => setSpeakingIndex(null);
   
    window.speechSynthesis.speak(utterance);
    speechSynthesisRef.current = utterance;
    setSpeakingIndex(index);
  };

  const sendMessage = async (userMessage) => {
    if (!userMessage.trim()) return;
    const newUserMessage = { text: userMessage, type: "user" };
    setMessages((prev) => [...prev, newUserMessage]);
    setInput("");
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append("file_id", publicationId);
      formData.append("userMessage", userMessage);
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/chat_stream`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!response.ok) throw new Error("Error from backend");
      const result = await response.json();
      const answer = result.answer;
      const evidence = result.supporting_evidence;
      setMessages((prev) => [...prev, { text: answer, type: "bot" }]);
      if (Array.isArray(evidence)) {
        setHighlightData(evidence);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [...prev, { text: "Error fetching response", type: "bot" }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => sendMessage(input);
  const handleSuggestionClick = (text) => sendMessage(text);
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };
  const handleEdit = (text, index) => {
    setInput(text);
    const updated = [...messages];
    updated.splice(index, 1);
    setMessages(updated);
  };

  const parseMarkdownToTextRuns = (text, color) => {
    const runs = [];
    const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|[^*`]+)/g;
    const matches = text.match(regex) || [text];
    
    matches.forEach(segment => {
      if (segment.startsWith('**') && segment.endsWith('**')) {
        // Bold text
        runs.push(new TextRun({
          text: segment.slice(2, -2),
          bold: true,
          color: color,
        }));
      } else if (segment.startsWith('*') && segment.endsWith('*')) {
        // Italic text
        runs.push(new TextRun({
          text: segment.slice(1, -1),
          italics: true,
          color: color,
        }));
      } else if (segment.startsWith('`') && segment.endsWith('`')) {
        // Code text
        runs.push(new TextRun({
          text: segment.slice(1, -1),
          font: "Courier New",
          color: color,
        }));
      } else {
        // Normal text
        runs.push(new TextRun({
          text: segment,
          color: color,
        }));
      }
    });
    
    return runs;
  };

  // Parse message text into paragraphs (handles lists and line breaks)
  const parseMessageToParagraphs = (text, color) => {
    const paragraphs = [];
    
    // Split by double newlines or single newlines
    const lines = text.split(/\n/);
    
    lines.forEach((line) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return; // Skip empty lines
      
      // Check if it's a numbered list item (e.g., "1. ", "2. ")
      const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.*)$/);
      // Check if it's a bullet list item (e.g., "- ", "* ")
      const bulletMatch = trimmedLine.match(/^[-*]\s+(.*)$/);
      
      if (numberedMatch) {
        const [, number, content] = numberedMatch;
        const contentRuns = parseMarkdownToTextRuns(content, color);
        paragraphs.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `${number}. `,
                color: color,
              }),
              ...contentRuns
            ],
            spacing: { after: 120 },
            indent: { left: 360 }, // Indent list items
          })
        );
      } else if (bulletMatch) {
        const [, content] = bulletMatch;
        const contentRuns = parseMarkdownToTextRuns(content, color);
        paragraphs.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "• ",
                color: color,
              }),
              ...contentRuns
            ],
            spacing: { after: 120 },
            indent: { left: 360 },
          })
        );
      } else {
        // Regular paragraph
        const contentRuns = parseMarkdownToTextRuns(trimmedLine, color);
        paragraphs.push(
          new Paragraph({
            children: contentRuns,
            spacing: { after: 120 },
          })
        );
      }
    });
    
    return paragraphs;
  };

  const downloadChat = async () => {
    if (messages.length === 0) return;
    
    setDownloadStatus({ inProgress: true, error: false });
    
    try {
      const docChildren = [];
  
      docChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: "Chat History",
              bold: true,
              size: 32,
            }),
          ],
          heading: "Title",
          spacing: { after: 400 },
        })
      );
 
      messages.forEach((msg) => {
        const speaker = msg.type === "user" ? "You" : "Assistant";
        const color = msg.type === "user" ? "1e4de3" : "000000";
        
        // Add speaker label as separate paragraph
        docChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `${speaker}:`,
                bold: true,
                color: color,
              }),
            ],
            spacing: { after: 80 },
          })
        );
        
        // Parse message content into multiple paragraphs (handles lists)
        const contentParagraphs = parseMessageToParagraphs(msg.text, color);
        docChildren.push(...contentParagraphs);
        
        // Add spacing after each message
        docChildren.push(
          new Paragraph({
            children: [],
            spacing: { after: 200 },
          })
        );
      });
 
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: docChildren,
          },
        ],
      });
    
      await Packer.toBlob(doc).then((blob) => {
        saveAs(blob, "Pubvision's pubchat history.docx");
        setDownloadStatus({ inProgress: false, error: false });
      });
    } catch (error) {
      console.error("Download error:", error);
      setDownloadStatus({ inProgress: false, error: true });
    }
  };

  return (
    <div className="chat-box">
      <div className="chat-header">
        <h3>Chat</h3>
        <div className="header-content">
          {downloadStatus.inProgress && (
            <Stack sx={{ width: '100%', marginBottom: '10px' }} spacing={2}>
              <Alert severity="info">
                <AlertTitle>Downloading</AlertTitle>
                Downloading in progress
              </Alert>
            </Stack>
          )}
          {downloadStatus.error && (
            <Stack sx={{ width: '100%', marginBottom: '10px' }} spacing={2}>
              <Alert severity="error">
                <AlertTitle>Download Failed</AlertTitle>
                Unable to download. Please try after some time
              </Alert>
            </Stack>
          )}
          <div className="header-actions">
            <button className="download-btn" onClick={downloadChat} title="Download Chat">
              <MdOutlineFileDownload />
            </button>
            <select className="language-select">
              <option>English</option>
            </select>
          </div>
        </div>
      </div>
      <div className="chat-body">
        <div className="messages">
          {messages.map((msg, idx) => (
            <div key={idx} className={`message ${msg.type}`}>
              <div className="bubble">
                {msg.type === "bot" ? (
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                ) : (
                  msg.text
                )}
              </div>
              <div className={`msg-icons ${msg.type} ${speakingIndex === idx ? "speaking" : ""}`}>
                <FiCopy
                  className="icon"
                  title="Copy"
                  onClick={() => handleCopy(msg.text)}
                />
                {msg.type === "user" && (
                  <RiEditLine
                    className="icon"
                    title="Edit"
                    onClick={() => handleEdit(msg.text, idx)}
                  />
                )}
                {msg.type === "bot" && speechSupported && (
                  speakingIndex === idx ? (
                    <FiStopCircle
                      className="icon active"
                      title="Stop reading"
                      onClick={() => handleSpeak(msg.text, idx)}
                    />
                  ) : (
                    <FiVolume2
                      className="icon"
                      title="Read aloud"
                      onClick={() => handleSpeak(msg.text, idx)}
                    />
                  )
                )}
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="message bot">
              <div className="bubble">
                <div className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
        </div>
        {!loading && (
          <div className="suggestions">
            {suggestionsList.map((sugg, idx) => (
              <button key={idx} onClick={() => handleSuggestionClick(sugg)}>
                {sugg}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="chat-footer">
        <input
          type="text"
          placeholder="Ask me..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
        />
        <button className="send-btn" onClick={handleSend}>
          <span>&#9658;</span>
        </button>
      </div>
    </div>
  );
};

export default ChatBox;
